import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Lock, Loader2, ShieldAlert, CheckCircle2, X, RefreshCw,
  Sparkles, AlertTriangle, Info, CalendarClock, UserPlus,
} from "lucide-react";

// Draft Review page. Read-only inbox of system-proposed
// drafts across two kinds:
// - assignment      → Recommended assignment for a coverage gap
// - leave_decision  → Recommended approval for a clean leave request
// The only path that writes to live data is the Apply confirmation modal,
// which calls /api/drafts/{id}/apply → which routes through the existing
// /api/allocations writer (assignment) or the existing decide_leave handler
// (leave_decision). Reject never touches live data.

const ASSIGNMENT_REJECT_REASONS = [
  { code: "coverage_no_longer_required", label: "Coverage no longer required" },
  { code: "wrong_person_suggested",      label: "Wrong person suggested" },
  { code: "manager_will_handle_manually", label: "Manager will handle manually" },
  { code: "rota_changed",                label: "Rota changed" },
  { code: "other",                       label: "Other" },
];

// leave-specific taxonomy. Locked alongside the backend
// _LEAVE_REJECT_TAXONOMY so QA can pin these labels.
const LEAVE_REJECT_REASONS = [
  { code: "policy_judgement",              label: "Policy judgement" },
  { code: "coverage_already_resolved",     label: "Coverage already resolved" },
  { code: "employee_withdrew_request",     label: "Employee withdrew request" },
  { code: "manager_will_handle_manually",  label: "Manager will handle manually" },
  { code: "other",                         label: "Other" },
];

const KIND_META = {
  assignment: {
    label: "Assignment drafts",
    short: "assignment",
    Icon: UserPlus,
    listKind: "assignment",
    generateEndpoint: "/drafts/assignment/generate",
    rejectReasons: ASSIGNMENT_REJECT_REASONS,
    headlineEyebrow: "Recommended assignment",
    intro: "NextRota proposes draft assignments for coverage gaps with a strong-fit candidate. Approving a recommendation moves it into the weekly draft rota — nothing is live until you Publish the draft rota.",
    generateScopeDays: 7,
    generateButton: "Find coverage recommendations",
    emptyMessage: "Run the generator to look for coverage gaps in the next 7 days with a strong-fit candidate available. Approving a recommendation puts it into the draft rota — Publish (a separate action) is what writes to the live rota.",
    applyTitle: "Approve this recommendation into the draft rota?",
    applyButton: "Yes, approve into draft rota",
    applyBullets: [
      <><strong>This moves the recommendation into the weekly draft rota.</strong> It does NOT write to the live rota.</>,
      <>The draft cell will be marked <strong>Approved in draft</strong>. You still need to click <strong>Publish</strong> on the draft rota to push it live.</>,
      <>If no draft rota exists for that week yet, one will be created automatically.</>,
      <>If the underlying rota has changed since this draft was generated, Approve will be blocked and you&apos;ll be asked to re-run suggestions.</>,
    ],
  },
  leave_decision: {
    label: "Leave drafts",
    short: "leave",
    Icon: CalendarClock,
    listKind: "leave_decision",
    generateEndpoint: "/drafts/leave/generate",
    rejectReasons: LEAVE_REJECT_REASONS,
    headlineEyebrow: "Recommended leave approval",
    intro: "NextRota proposes leave approvals only for requests with no blocking findings, no coverage impact and no rule conflicts. The live leave request is never touched until a manager clicks Apply. Reject leaves the request untouched for manual decision.",
    generateScopeDays: 30,
    generateButton: "Find leave recommendations",
    emptyMessage: "Run the generator to look for clean leave requests in the next 30 days, those with no blocking findings, no coverage impact and no rule conflicts. Drafts never approve a leave until you click Apply.",
    applyTitle: "Approve this leave recommendation?",
    applyButton: "Yes, approve this leave request",
    applyBullets: [
      <><strong>This will approve the live leave request</strong> using the same path as a manual approval, same audit trail, same email to the employee.</>,
      <>The draft will be marked as <strong>Applied</strong> and cannot be applied again.</>,
      <>If the leave request was withdrawn, decided, or the policy set changed since this draft was generated, Apply will be blocked and you&apos;ll be asked to re-run suggestions.</>,
    ],
  },
};

const STATUS_META = {
  pending_review:    { label: "Pending review",     bg: "bg-amber-50",  border: "border-amber-300 border-dashed", text: "text-amber-800" },
  approved_in_draft: { label: "Approved in draft",  bg: "bg-blue-50",   border: "border-blue-300",                text: "text-blue-800" },
  applied:           { label: "Applied",            bg: "bg-emerald-50", border: "border-emerald-200",            text: "text-emerald-700" },
  rejected:          { label: "Rejected",           bg: "bg-slate-50",  border: "border-slate-200",              text: "text-slate-600" },
  superseded:        { label: "Superseded",         bg: "bg-slate-50",  border: "border-slate-200 border-dashed", text: "text-slate-500" },
  expired:           { label: "Expired",            bg: "bg-rose-50",   border: "border-rose-200 border-dashed",  text: "text-rose-700" },
};

function fmtDate(iso) {
  if (!iso) return "-";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
}

function expiresIn(expIso) {
  if (!expIso) return null;
  const ms = new Date(expIso) - new Date();
  if (ms <= 0) return { expired: true, text: "Expired, re-run suggestions" };
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days >= 1) return { expired: false, text: `Expires in ${days} day${days === 1 ? "" : "s"}` };
  const h = Math.max(1, Math.floor(ms / (1000 * 60 * 60)));
  return { expired: false, text: `Expires in ${h} hour${h === 1 ? "" : "s"}` };
}

export default function DraftsReviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialKind = (searchParams.get("kind") === "leave_decision") ? "leave_decision" : "assignment";
  const focusLeaveId = searchParams.get("leave_id") || null;
  const [kind, setKind] = useState(initialKind);
  const [filter, setFilter] = useState("pending_review");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [applyTarget, setApplyTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [revertTarget, setRevertTarget] = useState(null);

  // Lift the focused leave_id once initial fetch is done, keeps the URL clean.
  const meta = KIND_META[kind];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/drafts?status=${filter}&kind=${meta.listKind}&limit=100`);
      setData(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load drafts");
    } finally { setLoading(false); }
  }, [filter, meta.listKind]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function switchKind(nextKind) {
    setKind(nextKind);
    setFilter("pending_review");
    const next = new URLSearchParams(searchParams);
    next.set("kind", nextKind);
    if (nextKind !== "leave_decision") next.delete("leave_id");
    setSearchParams(next, { replace: true });
  }

  async function generate() {
    setGenerating(true);
    try {
      const today = new Date();
      const to = new Date(today); to.setDate(to.getDate() + meta.generateScopeDays);
      const fmt = (d) => d.toISOString().slice(0, 10);
      const { data } = await api.post(meta.generateEndpoint, {
        from_date: fmt(today), to_date: fmt(to),
      });
      const parts = [];
      if (data.created_count) parts.push(`${data.created_count} draft${data.created_count === 1 ? "" : "s"} created`);
      if (data.skipped_dedup) parts.push(`${data.skipped_dedup} already pending`);
      if (data.skipped_non_strong) parts.push(`${data.skipped_non_strong} excluded, not strong-fit`);
      if (data.skipped_not_strong)  parts.push(`${data.skipped_not_strong} excluded, needs manager review`);
      if (data.skipped_serious_warnings) parts.push(`${data.skipped_serious_warnings} excluded, coverage or policy warning`);
      if (data.skipped_no_category) parts.push(`${data.skipped_no_category} excluded, no specific category`);
      toast.success(parts.join(" · ") || "No new drafts produced.");
      setFilter("pending_review");
      await fetchData();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to generate drafts");
    } finally { setGenerating(false); }
  }

  // Filter items locally for focused leave_id (deep-link from Leave page).
  const items = (data?.items || []).filter((d) => {
    if (kind === "leave_decision" && focusLeaveId) {
      return d.leave_request_id === focusLeaveId;
    }
    return true;
  });

  return (
    <div data-testid="drafts-review-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow flex items-center gap-2">
            Automation
            <span className="text-[10px] uppercase tracking-widest text-amber-800 px-1.5 py-0.5 rounded bg-amber-100 border border-amber-200">
              Draft · Not live
            </span>
          </div>
          <h1 className="font-display text-4xl mt-1 flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-[#2563EB]" />
            Drafts awaiting review
          </h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
            {meta.intro}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="drafts-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={generate} disabled={generating}
                  className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                  data-testid="drafts-generate-btn">
            {generating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
            {meta.generateButton}
          </Button>
        </div>
      </div>

      {/* Kind tabs (, top-level segmentation) */}
      <div className="flex gap-1 mb-4 border-b border-slate-200" data-testid="drafts-kind-tabs">
        {Object.entries(KIND_META).map(([k, m]) => {
          const KIcon = m.Icon;
          const active = kind === k;
          return (
            <button key={k} type="button" onClick={() => switchKind(k)}
                    className={`px-4 py-2 text-sm border-b-2 -mb-px flex items-center gap-2 ${
                      active
                        ? "border-[#2563EB] text-[#1D4ED8] font-medium"
                        : "border-transparent text-[#4B5773] hover:text-[#0B1A36]"}`}
                    data-testid={`drafts-kind-${m.short}`}>
              <KIcon className="h-4 w-4" /> {m.label}
            </button>
          );
        })}
      </div>

      {focusLeaveId && kind === "leave_decision" && (
        <div className="mb-3 rounded border border-[#BFD3FB] bg-[#F0F6FF] px-3 py-2 flex items-center gap-2 text-xs text-[#1D4ED8]"
             data-testid="drafts-focus-banner">
          <Info className="h-3.5 w-3.5" />
          Filtered to one leave request from the Leave page.
          <button type="button" onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.delete("leave_id");
            setSearchParams(next, { replace: true });
          }} className="ml-auto underline hover:text-[#0B1A36]" data-testid="drafts-focus-clear">
            Show all leave drafts
          </button>
        </div>
      )}

      <div className="mb-5 p-4 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE] text-xs text-[#1F2A44]">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-[#2563EB] mt-0.5 shrink-0" />
          <div className="leading-relaxed">
            <span className="font-medium text-[#0B1A36]">What you&apos;re looking at.</span>{" "}
            {kind === "leave_decision" ? (
              <>These are <strong>leave approval drafts</strong>, recommended approvals that <strong>require manager approval</strong>. Clicking <em>Approve</em> approves the live leave request using the same path as a manual approval. Reject leaves the request untouched. Drafts that age past 7 days expire automatically and must be regenerated.</>
            ) : (
              <>These are <strong>assignment recommendations</strong>. Clicking <em>Approve</em> moves the recommendation into the weekly draft rota for that week (creating one if it does not exist). Nothing reaches the live rota until you click <em>Publish</em> on the draft rota. Drafts that age past 7 days expire automatically and must be regenerated.</>
            )}
          </div>
        </div>
      </div>

      {/* Status sub-tabs */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="drafts-status-tabs">
        {[
          { key: "pending_review",    label: `Pending (${data?.counts?.pending_review ?? "·"})` },
          ...(kind === "assignment" ? [{
            key: "approved_in_draft",
            label: `Approved in draft (${data?.counts?.approved_in_draft ?? "·"})`,
          }] : []),
          { key: "applied",        label: `Applied (${data?.counts?.applied ?? "·"})` },
          { key: "rejected",       label: `Rejected (${data?.counts?.rejected ?? "·"})` },
          { key: "expired",        label: `Expired (${data?.counts?.expired ?? "·"})` },
        ].map((t) => (
          <button key={t.key} type="button" onClick={() => setFilter(t.key)}
                  className={`px-3 py-1 rounded text-xs border ${filter === t.key
                    ? "bg-[#DBE7FE] border-[#BFD3FB] text-[#1D4ED8]"
                    : "bg-white border-slate-200 text-[#4B5773] hover:border-slate-300"}`}
                  data-testid={`drafts-filter-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-[#4B5773]">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading drafts…
        </div>
      ) : items.length === 0 ? (
        <EmptyState filter={filter} kindMeta={meta} onGenerate={generate} generating={generating} focused={!!focusLeaveId && kind === "leave_decision"} />
      ) : (
        <ul className="space-y-3" data-testid="drafts-list">
          {items.map((d) => (
            <DraftCard key={d.draft_id} draft={d} kindMeta={meta}
                       onApply={() => setApplyTarget(d)}
                       onReject={() => setRejectTarget(d)}
                       onRevertAuto={() => setRevertTarget(d)} />
          ))}
        </ul>
      )}

      {applyTarget && (
        <ApplyConfirmModal draft={applyTarget} kindMeta={meta}
                            onClose={() => setApplyTarget(null)}
                            onApplied={async () => { setApplyTarget(null); await fetchData(); }} />
      )}
      {rejectTarget && (
        <RejectModal draft={rejectTarget} kindMeta={meta}
                     onClose={() => setRejectTarget(null)}
                     onRejected={async () => { setRejectTarget(null); await fetchData(); }} />
      )}
      {revertTarget && (
        <RevertAutoApplyModal draft={revertTarget}
                                onClose={() => setRevertTarget(null)}
                                onReverted={async () => { setRevertTarget(null); await fetchData(); }} />
      )}
    </div>
  );
}

function EmptyState({ filter, kindMeta, onGenerate, generating, focused }) {
  if (focused) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center"
           data-testid="drafts-empty-focused">
        <p className="text-sm text-[#4B5773]">No draft pending for this leave request.</p>
      </div>
    );
  }
  if (filter === "pending_review") {
    return (
      <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50/40 p-8 text-center"
           data-testid="drafts-empty-pending">
        <Sparkles className="h-8 w-8 text-amber-600 mx-auto mb-2" />
        <h3 className="font-medium text-[#0B1A36] text-sm">No drafts pending review</h3>
        <p className="text-xs text-[#4B5773] mt-1 max-w-md mx-auto">
          {kindMeta.emptyMessage}
        </p>
        <Button size="sm" className="mt-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                onClick={onGenerate} disabled={generating}
                data-testid="drafts-empty-generate-btn">
          {generating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
          {kindMeta.generateButton}
        </Button>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center"
         data-testid={`drafts-empty-${filter}`}>
      <p className="text-sm text-[#4B5773]">No {STATUS_META[filter]?.label?.toLowerCase() || filter} drafts.</p>
    </div>
  );
}

function DraftCard({ draft, kindMeta, onApply, onReject, onRevertAuto }) {
  const meta = STATUS_META[draft.status] || STATUS_META.pending_review;
  const exp = expiresIn(draft.expires_at);
  const isPending = draft.status === "pending_review";
  const isLeave = draft.kind === "leave_decision";
  const isAutoApplied = !!draft.applied_by_system;
  const sourceLabel = draft.status === "applied"
    ? (isAutoApplied ? "Auto-applied by NextRota" : "Approved by manager")
    : null;
  // Revert is only available while the target_date is still in the future.
  const targetInFuture = (() => {
    if (!draft.target_date) return false;
    try {
      const today = new Date(); today.setHours(0,0,0,0);
      const td = new Date(draft.target_date + "T00:00:00");
      return td > today;
    } catch { return false; }
  })();
  return (
    <li className={`rounded-lg border-2 ${meta.border} ${meta.bg} p-4`}
        data-testid={`draft-card-${draft.draft_id}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-medium text-amber-900 px-2 py-0.5 rounded bg-amber-100 border border-amber-300">
          <Lock className="h-3 w-3" /> Draft · Not live
        </span>
        <span className={`text-[10px] uppercase tracking-widest ${meta.text}`}>
          {meta.label}
        </span>
        {isAutoApplied && draft.status === "applied" && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-blue-900 px-1.5 py-0.5 rounded bg-blue-100 border border-blue-300"
                data-testid="draft-source-auto"
                title="This recommendation was auto-applied by NextRota. Review the qualifying reasons in the audit trail below.">
            <Sparkles className="h-3 w-3" /> Auto-applied
          </span>
        )}
        {!isAutoApplied && draft.status === "applied" && (
          <span className="text-[10px] uppercase tracking-widest text-slate-700 px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200"
                data-testid="draft-source-manual">
            Approved by manager
          </span>
        )}
        {draft.confidence_band && (
          <span className="text-[10px] uppercase tracking-widest text-emerald-800 px-1.5 py-0.5 rounded bg-emerald-100 border border-emerald-200">
            {draft.confidence_band} fit
          </span>
        )}
        <span className="text-[11px] text-[#64748B] ml-auto">
          {exp && exp.expired ? (
            <span className="text-rose-700">{exp.text}</span>
          ) : exp ? exp.text : null}
        </span>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap" data-testid={`draft-headline-${draft.draft_id}`}>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#64748B] mb-1">
            {kindMeta.headlineEyebrow}
          </div>
          <div className="text-2xl font-medium text-[#0B1A36] leading-tight">
            {draft.suggested_employee_name}
          </div>
          {isLeave ? (
            <div className="text-sm text-[#0B1A36] mt-0.5">
              Leave request {fmtDate(draft.target_date)}
              {draft.target_end_date && draft.target_end_date !== draft.target_date
                ? ` → ${fmtDate(draft.target_end_date)}` : ""}
            </div>
          ) : (
            <>
              <div className="text-sm text-[#0B1A36] mt-0.5">
                {draft.target_category_name || draft.policy_name || "Coverage slot"}
                {draft.target_team_name ? <span className="text-[#4B5773]"> · {draft.target_team_name}</span> : null}
              </div>
              <div className="text-sm text-[#4B5773] mt-0.5">{fmtDate(draft.target_date)}</div>
            </>
          )}
        </div>
        <div className="text-right text-xs text-[#4B5773]">
          {draft.confidence_band && (
            <div className="text-[10px] uppercase tracking-widest text-emerald-800 px-1.5 py-0.5 rounded bg-emerald-100 border border-emerald-200 inline-block">
              {draft.confidence_band} fit
            </div>
          )}
          {draft.policy_name && (
            <div className="mt-1 text-[11px]">{draft.policy_name}</div>
          )}
        </div>
      </div>

      <div className="mt-3">
        <p className="text-sm text-[#0B1A36] leading-relaxed" data-testid={`draft-rationale-${draft.draft_id}`}>
          {draft.rationale_text}
        </p>
        {(draft.reason_chips || []).length > 0 && (
          <ul className="flex flex-wrap gap-1.5 mt-2">
            {draft.reason_chips.map((r, i) => (
              <li key={i} className="text-[11px] text-emerald-800 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200">
                <CheckCircle2 className="h-3 w-3 inline mr-1" />
                {typeof r === "string" ? r : (r.text || r.label || JSON.stringify(r))}
              </li>
            ))}
          </ul>
        )}
        {(draft.watch_outs || []).length > 0 && (
          <ul className="flex flex-wrap gap-1.5 mt-1.5">
            {draft.watch_outs.map((w, i) => (
              <li key={i} className="text-[11px] text-amber-800 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200">
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                {typeof w === "string" ? w : (w.text || w.label || JSON.stringify(w))}
              </li>
            ))}
          </ul>
        )}
      </div>

      {isPending && (
        <div className="mt-3 pt-3 border-t border-amber-200/60 flex items-center gap-2">
          <Button size="sm" onClick={onApply}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid={`draft-apply-btn-${draft.draft_id}`}>
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
            {isLeave ? "Approve leave recommendation" : "Approve recommendation"}
          </Button>
          <Button size="sm" variant="outline" onClick={onReject}
                  className="border-slate-300 text-[#4B5773]"
                  data-testid={`draft-reject-btn-${draft.draft_id}`}>
            <X className="h-3.5 w-3.5 mr-1" />
            Reject…
          </Button>
          <span className="text-[11px] text-[#64748B] ml-auto">
            {isLeave
              ? "Requires manager approval, drafts are not live until you click Approve."
              : "Approve moves this into the draft rota. Publish (separate) writes to the live rota."}
          </span>
        </div>
      )}
      {draft.status === "approved_in_draft" && (
        <div className="mt-3 pt-3 border-t border-blue-200/60 flex items-center justify-between gap-3 flex-wrap"
             data-testid={`draft-approved-in-draft-banner-${draft.draft_id}`}>
          <p className="text-xs text-blue-900">
            Approved into the draft rota. Not live until you publish the draft rota.
          </p>
          {draft.period_id && (
            <a href={`/app/drafts/rota/${draft.period_id}`}
               className="text-xs underline text-blue-800 hover:text-blue-900"
               data-testid={`draft-open-period-${draft.draft_id}`}>
              Open draft rota →
            </a>
          )}
        </div>
      )}
      {draft.status === "applied" && draft.applied_allocation_id && (
        <div className="mt-3 pt-3 border-t border-emerald-200/60 space-y-2">
          <p className="text-xs text-emerald-800">
            Applied to the live rota. Allocation id: <code className="font-mono">{draft.applied_allocation_id}</code>
          </p>
          {isAutoApplied && (
            <div className="rounded border border-blue-200 bg-blue-50/60 px-3 py-2"
                 data-testid={`draft-auto-applied-panel-${draft.draft_id}`}>
              <div className="text-[11px] uppercase tracking-widest text-blue-900 mb-1">
                Auto-apply audit
              </div>
              <p className="text-xs text-[#0B1A36]">
                NextRota auto-applied this recommendation
                {draft.applied_at ? ` at ${new Date(draft.applied_at).toLocaleString("en-GB", {day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit", hour12:false})}` : ""}.
                Every safety check passed before the apply.
              </p>
              {(draft.auto_apply_qualifying_reasons || []).length > 0 && (
                <details className="mt-1">
                  <summary className="text-[11px] text-blue-900 cursor-pointer"
                           data-testid={`draft-auto-applied-reasons-toggle-${draft.draft_id}`}>
                    Why this qualified ({(draft.auto_apply_qualifying_reasons || []).length})
                  </summary>
                  <ul className="mt-1 text-[11px] text-[#1F2A44] list-disc list-inside space-y-0.5">
                    {draft.auto_apply_qualifying_reasons.map((r, i) => (
                      <li key={i} className="font-mono">{r}</li>
                    ))}
                  </ul>
                </details>
              )}
              {targetInFuture && (
                <div className="mt-2">
                  <Button size="sm" variant="outline"
                          className="h-7 text-xs border-rose-300 text-rose-800 hover:bg-rose-50"
                          onClick={onRevertAuto}
                          data-testid={`draft-revert-auto-btn-${draft.draft_id}`}>
                    <X className="h-3 w-3 mr-1" /> Revert this auto-apply
                  </Button>
                  <span className="ml-2 text-[11px] text-[#4B5773]">
                    Available because the shift is still in the future.
                  </span>
                </div>
              )}
              {!targetInFuture && (
                <p className="text-[11px] text-[#4B5773] mt-1">
                  Past shift, use the normal rota correction path on the Roster page.
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {draft.status === "applied" && isLeave && (
        <p className="mt-3 pt-3 border-t border-emerald-200/60 text-xs text-emerald-800">
          Approved the live leave request. Leave id: <code className="font-mono">{draft.leave_request_id}</code>
        </p>
      )}
      {draft.status === "rejected" && (
        <p className="mt-3 pt-3 border-t border-slate-200/60 text-xs text-[#4B5773]">
          Rejected, <strong>{(kindMeta.rejectReasons.find(r => r.code === draft.decision_reason) || {}).label || draft.decision_reason}</strong>
          {draft.decision_note ? ` · "${draft.decision_note}"` : ""}
        </p>
      )}
    </li>
  );
}

function ApplyConfirmModal({ draft, kindMeta, onClose, onApplied }) {
  const [submitting, setSubmitting] = useState(false);

  async function confirm() {
    setSubmitting(true);
    try {
      // Assignment recommendations approve INTO the draft rota.
      // Leave drafts still use the historic apply path (the leave
      // decision pipeline is the live write surface for leave).
      const isLeave = draft.kind === "leave_decision";
      const endpoint = isLeave
        ? `/drafts/${draft.draft_id}/apply`
        : `/drafts/${draft.draft_id}/approve`;
      const { data } = await api.post(endpoint, { confirm: true });
      if (isLeave) {
        toast.success(`Leave approved. Leave id: ${data.applied_leave_request_id || draft.leave_request_id}.`);
      } else {
        const pid = data?.period_id;
        const newPeriod = data?.auto_created_period;
        toast.success(
          newPeriod
            ? "Approved into a new draft rota. Open the draft rota to review and Publish when ready."
            : "Approved into the draft rota. Open the draft rota to review and Publish when ready.",
          pid ? {
            action: {
              label: "Open draft rota",
              onClick: () => { window.location.href = `/app/drafts/rota/${pid}`; },
            },
          } : undefined,
        );
      }
      onApplied();
    } catch (err) {
      const msg = formatError(err.response?.data?.detail) || "Failed to approve draft";
      toast.error(msg);
      if (err.response?.status === 409) onApplied();
    } finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
         data-testid="apply-confirm-modal">
      <div className="bg-white rounded-lg shadow-2xl w-[520px] max-w-[92vw] overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <h3 className="font-medium text-base text-[#0B1A36]">{kindMeta.applyTitle}</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 hover:text-slate-700"
                  data-testid="apply-confirm-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm text-[#0B1A36]">
          <p className="font-medium">{draft.rationale_text}</p>
          <ul className="text-xs text-[#4B5773] space-y-1 list-disc list-inside">
            {kindMeta.applyBullets.map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center gap-2 justify-end">
          <Button size="sm" variant="outline" onClick={onClose} data-testid="apply-confirm-cancel">
            Cancel
          </Button>
          <Button size="sm" onClick={confirm} disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="apply-confirm-submit">
            {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
            {kindMeta.applyButton}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RejectModal({ draft, kindMeta, onClose, onRejected }) {
  const [reasonCode, setReasonCode] = useState(kindMeta.rejectReasons[0].code);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function confirm() {
    if (reasonCode === "other" && !note.trim()) {
      toast.error("A short note is required when 'Other' is selected.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/drafts/${draft.draft_id}/reject`, { reason_code: reasonCode, note: note.trim() || null });
      toast.success(draft.kind === "leave_decision"
        ? "Draft rejected. The live leave request is unchanged."
        : "Draft rejected. The live rota is unchanged.");
      onRejected();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to reject draft");
    } finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
         data-testid="reject-modal">
      <div className="bg-white rounded-lg shadow-2xl w-[520px] max-w-[92vw] overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-slate-600" />
          <h3 className="font-medium text-base text-[#0B1A36]">Reject this draft?</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 hover:text-slate-700"
                  data-testid="reject-modal-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm text-[#0B1A36]">
          <p className="text-xs text-[#4B5773]">
            {draft.kind === "leave_decision"
              ? "Rejecting a draft never touches the live leave request."
              : "Rejecting a draft never touches your live rota."}
          </p>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[#64748B] block mb-1">Reason</label>
            <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                    data-testid="reject-reason-select">
              {kindMeta.rejectReasons.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[#64748B] block mb-1">
              Note {reasonCode === "other" && <span className="text-rose-600">(required)</span>}
            </label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                      placeholder="Add context (optional, except for 'Other')"
                      data-testid="reject-note-input" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center gap-2 justify-end">
          <Button size="sm" variant="outline" onClick={onClose} data-testid="reject-modal-cancel">
            Cancel
          </Button>
          <Button size="sm" onClick={confirm} disabled={submitting}
                  className="bg-slate-700 hover:bg-slate-800 text-white"
                  data-testid="reject-modal-submit">
            {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <X className="h-3.5 w-3.5 mr-1" />}
            Reject draft
          </Button>
        </div>
      </div>
    </div>
  );
}

function RevertAutoApplyModal({ draft, onClose, onReverted }) {
  const [submitting, setSubmitting] = useState(false);
  async function confirm() {
    setSubmitting(true);
    try {
      await api.post(`/drafts/${draft.draft_id}/revert-auto-apply`);
      toast.success("Auto-apply reverted. The allocation has been removed from the live rota.");
      onReverted();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Revert failed");
    } finally { setSubmitting(false); }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
         data-testid="revert-auto-apply-modal">
      <div className="bg-white rounded-lg shadow-2xl w-[520px] max-w-[92vw] overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-rose-600" />
          <h3 className="font-medium text-base text-[#0B1A36]">Revert this auto-apply?</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 hover:text-slate-700"
                  data-testid="revert-auto-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm text-[#0B1A36]">
          <p>
            NextRota auto-applied this recommendation for <strong>{draft.suggested_employee_name}</strong>
            {" "}on <strong>{fmtDate(draft.target_date)}</strong>. Reverting will remove the
            allocation from the live rota and mark this draft as rejected.
          </p>
          <ul className="text-xs text-[#4B5773] space-y-1 list-disc list-inside">
            <li>The full audit chain (auto-apply → revert by you) is preserved.</li>
            <li>The coverage gap will re-appear in the next engine run if still relevant.</li>
            <li>This action is only available because the shift is still in the future.</li>
          </ul>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center gap-2 justify-end">
          <Button size="sm" variant="outline" onClick={onClose} data-testid="revert-auto-cancel">
            Cancel
          </Button>
          <Button size="sm" onClick={confirm} disabled={submitting}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="revert-auto-confirm-btn">
            {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <X className="h-3.5 w-3.5 mr-1" />}
            Yes, revert this auto-apply
          </Button>
        </div>
      </div>
    </div>
  );
}

